import React, { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";

/* ─── Variant configs ────────────────────────────────────────────────────── */
const VARIANTS = {
  promo:   { color: "#ffc107", bg: "rgba(255,193,7,.07)",   border: "rgba(255,193,7,.3)"   },
  new:     { color: "#00e676", bg: "rgba(0,230,118,.07)",   border: "rgba(0,230,118,.3)"   },
  info:    { color: "#4fc3f7", bg: "rgba(79,195,247,.07)",  border: "rgba(79,195,247,.3)"  },
  event:   { color: "#ba68c8", bg: "rgba(186,104,200,.07)", border: "rgba(186,104,200,.3)" },
  warning: { color: "#ff9800", bg: "rgba(255,152,0,.07)",   border: "rgba(255,152,0,.3)"   },
  danger:  { color: "#ff3b5c", bg: "rgba(255,59,92,.07)",   border: "rgba(255,59,92,.3)"   },
};
const getVariant = (v) => VARIANTS[v] || VARIANTS.info;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideDown = keyframes`from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}`;
const pulse     = keyframes`0%,100%{opacity:.4}50%{opacity:1}`;

/* ─── Styled components ──────────────────────────────────────────────────── */
const Wrapper = styled.div`
  display: flex; align-items: center; gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: ${({$variant}) => getVariant($variant).bg};
  border: 1px solid ${({$variant}) => getVariant($variant).border};
  margin-bottom: 1rem;
    margin-top: 1rem;
  animation: ${slideDown} .35s ease both;
  position: relative;
  overflow: hidden;

  /* Liseré gauche animé */
  &::before {
    content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: ${({$variant}) => getVariant($variant).color};
    animation: ${pulse} 2.5s ease infinite;
  }

  @media(max-width: 640px) {
    flex-direction: column; align-items: flex-start;
    gap: .85rem; padding: .9rem 1rem;
  }
`;

const Icon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  background: ${({theme}) => theme.bgCard};
  border-radius: 10px;
  border: 1px solid ${({$variant}) => getVariant($variant).border};
`;

const Content = styled.div`
  flex: 1; min-width: 0;
`;

const Title = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: .92rem;
  font-weight: 700;
  color: ${({theme}) => theme.text};
  letter-spacing: -.01em;
  margin-bottom: .2rem;
  display: flex; align-items: center; gap: .5rem;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .55rem; font-weight: 700;
  padding: .15rem .5rem;
  border-radius: 3px;
  background: ${({$variant}) => getVariant($variant).color};
  color: #000;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const Message = styled.div`
  font-size: .78rem;
  color: ${({theme}) => theme.textDim};
  line-height: 1.5;
  strong { color: ${({theme}) => theme.text}; }
  a { color: ${({$variant}) => getVariant($variant).color}; text-decoration: underline; }
`;

const Actions = styled.div`
  display: flex; align-items: center; gap: .5rem;
  flex-shrink: 0;
  @media(max-width: 640px) { width: 100%; }
`;

const CTAButton = styled.button`
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .55rem 1rem;
  border-radius: 8px;
  border: none;
  background: ${({$variant}) => getVariant($variant).color};
  color: #000;
  font-family: ${({theme}) => theme.fontBody};
  font-size: .76rem; font-weight: 700;
  letter-spacing: .02em;
  cursor: pointer;
  transition: all .15s;
  text-decoration: none;
  white-space: nowrap;
  &:hover { opacity: .88; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  @media(max-width: 640px) { flex: 1; justify-content: center; }
`;

const DismissBtn = styled.button`
  width: 28px; height: 28px;
  border-radius: 50%;
  border: none; background: transparent;
  color: ${({theme}) => theme.textMuted};
  cursor: pointer; font-size: .85rem;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
  flex-shrink: 0;
  &:hover {
    background: ${({theme}) => theme.bgHover};
    color: ${({theme}) => theme.text};
  }
`;

/* ─── Dismiss memory (localStorage) ──────────────────────────────────────── */
const STORAGE_KEY = "evenmt_dismissed_announcements";

function getDismissedIds() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
  catch { return []; }
}
function persistDismissed(ids) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); }
  catch {}
}

/* ─── i18n helper ────────────────────────────────────────────────────────── */
function tr(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.fr || value.en || Object.values(value)[0] || "";
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function AnnouncementBanner({
  id,                    // string unique — pour mémoriser le dismiss
  type = "info",         // promo | new | info | event | warning | danger
  icon = "📢",           // emoji ou string
  title,                 // string ou {fr,en,es,de,pt,ar}
  message,               // string ou objet i18n (HTML safe via <strong> et <a>)
  badge,                 // tag court optionnel ("NEW", "PROMO"…)
  cta,                   // { label, onClick?, href?, target? }
  dismissible = true,    // afficher le bouton ✕
  expires,               // Date | string ISO — auto-hide après cette date
  onDismiss,             // callback optionnel appelé au dismiss
}) {
  const { lang } = useI18n();

  /* État dismiss avec mémoire localStorage */
  const [dismissed, setDismissed] = useState(() => {
    if (!id || !dismissible) return false;
    return getDismissedIds().includes(id);
  });

  /* Auto-hide si expirée */
  const isExpired = useMemo(() => {
    if (!expires) return false;
    const d = expires instanceof Date ? expires : new Date(expires);
    return !isNaN(d.getTime()) && d.getTime() < Date.now();
  }, [expires]);

  if (dismissed || isExpired) return null;

  const handleDismiss = () => {
    if (id) {
      const list = getDismissedIds();
      if (!list.includes(id)) {
        list.push(id);
        persistDismissed(list);
      }
    }
    setDismissed(true);
    onDismiss?.();
  };

  const handleCta = (e) => {
    if (cta?.onClick) {
      e.preventDefault();
      cta.onClick();
    }
  };

  return (
    <Wrapper $variant={type}>
      {icon && <Icon $variant={type}>{icon}</Icon>}

      <Content>
        {title && (
          <Title>
            {tr(title, lang)}
            {badge && <Badge $variant={type}>{tr(badge, lang)}</Badge>}
          </Title>
        )}
        {message && (
          <Message
            $variant={type}
            dangerouslySetInnerHTML={{ __html: tr(message, lang) }}
          />
        )}
      </Content>

      <Actions>
        {cta && (
          <CTAButton
            as={cta.href ? "a" : "button"}
            href={cta.href}
            target={cta.target}
            rel={cta.target === "_blank" ? "noopener noreferrer" : undefined}
            onClick={handleCta}
            $variant={type}
          >
            {tr(cta.label, lang)} →
          </CTAButton>
        )}
        {dismissible && (
          <DismissBtn onClick={handleDismiss} title="Fermer" aria-label="Dismiss">
            ✕
          </DismissBtn>
        )}
      </Actions>
    </Wrapper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STACK COMPONENT — affiche plusieurs annonces les unes sous les autres
═══════════════════════════════════════════════════════════════════════════ */
export function AnnouncementStack({ items = [], onAction }) {
  return items.map((ann) => {
    /* Si l'annonce a un `action` string au lieu d'onClick, on le map */
    const cta = ann.cta && ann.cta.action && !ann.cta.onClick
      ? { ...ann.cta, onClick: () => onAction?.(ann.cta.action, ann) }
      : ann.cta;

    return <AnnouncementBanner key={ann.id || ann.title} {...ann} cta={cta} />;
  });
}
